import React, { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Card, CardContent, Typography, Box } from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import { makeStyles } from "@material-ui/core/styles";
import { useRccTypography } from "../typography";
import { rccColors } from "../colors";
import { countOrACVFormatterMillion } from "../../util/customFunctions";
import { appService } from "../../App/app.service";

/**
 * Qtr Projection card
 *
 * This implementation closely follows the Figma layout:
 * - Outer white card with 12px radius and soft shadow
 * - Left container: Qtr Projection title with H, M, L values and percentage tags
 * - Right container: Coverage Ratio and Qualified Pipe metrics
 */
const useStyles = makeStyles((theme) => ({
  card: {
    borderRadius: "12px",
    backgroundColor: "#FFFFFF",
    boxShadow: "6px 6px 54px 0 rgba(0, 0, 0, 0.05)",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    transition: "transform 0.2s, box-shadow 0.2s",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
    },
  },
  contentRoot: {
    display: "flex",
    flex: "1 0 auto",
    alignItems: "stretch",
    padding: "16px 12px",
    width: "100%",
    boxSizing: "border-box",
    "&:last-child": {
      paddingBottom: "16px",
    },
  },
  leftContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    gap: "8px",
    flex: "1",
  },
  projectionsGrid: {
    display: "grid",
    // Column 1 (Label), Column 2 (Value), Column 3 (Pill)
    gridTemplateColumns: "max-content max-content max-content",
    alignItems: "center",
    columnGap: "12px", // Controls space between H: and 0.52M, and 0.52M and %
    rowGap: "8px", // Controls space between the H, M, and L rows
    marginTop: "auto", // Pushes this section to the bottom of the container if needed
  },
  labelAlign: {
    justifySelf: "end", // Pushes the text to the right side of the grid cell
    textAlign: "right",
  },
  valueAlign: {
    justifySelf: "end", // Pushes value to right edge of column (aligns numbers)
    textAlign: "right",
  },
  rightContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between", // Keep space-between to push the bottom metric down to the card edge
    alignItems: "flex-start",
    height: "100%",
    paddingLeft: "8px",
  },
  projectionRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    alignSelf: "stretch",
  },
  pill: {
    display: "flex",
    padding: "2px 8px",
    alignItems: "center",
    gap: "2px",
    borderRadius: "100px",
    width: "fit-content", // Ensure pill doesn't stretch
  },
  pillGreen: {
    backgroundColor: rccColors.green[50],
    color: rccColors.green[700],
  },
  pillLightGreen: {
    backgroundColor: rccColors.lightGreen[50],
    color: rccColors.lightGreen[700],
  },
  pillYellow: {
    backgroundColor: rccColors.lime[50],
    color: rccColors.lime[700],
  },
  metricBox: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "flex-start",
    gap: "4px",
    alignSelf: "stretch",
  },
  metricOval: {
    display: "flex",
    padding: "2px 8px",
    alignItems: "center",
    gap: "2px",
    borderRadius: "100px",
    backgroundColor: rccColors.blue[50],
  },
}));

function QtrProjectionCard({ title, redirectToDashboard, onDrilldown }) {
  const classes = useStyles();
  const typography = useRccTypography();
  const dispatch = useDispatch();
  const fetchingRefs = useRef({
    homepageData: false,
    ensembleData: false,
    pipelineCoverageCFQ: false,
  });
  const user = useSelector((state) => state.app.user || {});

  // Fetch data directly from Redux
  const homepageData = useSelector((state) => state.app.homepageData || "");
  const ensembleData = useSelector((state) => state.app.EnsembleData || "");
  const pipelineCoverageCFQ = useSelector(
    (state) => state.app.PipelineCoverageHomeCFQ || ""
  );
  const differentQtrData = homepageData?.differentQtrData || {};

  // Check if any required data is loading
  const isLoading =
    homepageData === "" || ensembleData === "" || pipelineCoverageCFQ === "";

  // Fetch data if not available
  useEffect(() => {
    if (homepageData === "" && !fetchingRefs.current.homepageData) {
      fetchingRefs.current.homepageData = true;
      dispatch({ type: "get_homepage_data_request" });
      appService.homepageData().then(
        (resp) => {
          fetchingRefs.current.homepageData = false;
          dispatch({
            type: "get_homepage_data_success",
            homepageData: {
              data: resp.message.recordsets,
              columns: resp.message.columns,
              quartersToUse: resp.message.quartersToUse,
              differentQtrData: resp.message.differentQtrData,
            },
          });
        },
        (err) => {
          fetchingRefs.current.homepageData = false;
          dispatch({ type: "get_homepage_data_success", error: err });
        }
      );
    }

    if (
      pipelineCoverageCFQ === "" &&
      !fetchingRefs.current.pipelineCoverageCFQ
    ) {
      fetchingRefs.current.pipelineCoverageCFQ = true;
      dispatch({
        type: `get_generic_pipeline_coverage_data_CFQ_home_request`,
      });
      appService.getGenericPipelineCoverageData([], "CFQ").then(
        (json) => {
          fetchingRefs.current.pipelineCoverageCFQ = false;
          dispatch({
            type: `get_generic_pipeline_coverage_data_CFQ_home_success`,
            json,
          });
        },
        (error) => {
          fetchingRefs.current.pipelineCoverageCFQ = false;
          if (typeof error === "object")
            dispatch({
              type: `get_generic_pipeline_coverage_data_CFQ_home_failure`,
              error: "Something went wrong",
            });
          else
            dispatch({
              type: `get_generic_pipeline_coverage_data_CFQ_home_failure`,
              error,
            });
        }
      );
    }

    if (ensembleData === "" && !fetchingRefs.current.ensembleData) {
      fetchingRefs.current.ensembleData = true;
      dispatch({
        type: `get_ensemble_home_request`,
      });
      appService.getEnsembleData().then(
        (json) => {
          fetchingRefs.current.ensembleData = false;
          dispatch({
            type: `get_ensemble_home_success`,
            json,
          });
        },
        (error) => {
          fetchingRefs.current.ensembleData = false;
          if (typeof error === "object")
            dispatch({
              type: `get_ensemble_home_failure`,
              error: "Something went wrong",
            });
          else dispatch({ type: "get_quota_attainment_data_failure", error });
        }
      );
    }
  }, [homepageData, pipelineCoverageCFQ, ensembleData, dispatch]);

  // Show loading state with skeleton
  if (isLoading) {
    return (
      <Card className={classes.card} style={{ height: "100%" }}>
        <CardContent className={classes.contentRoot} style={{ height: "100%" }}>
          <Skeleton
            variant="rect"
            width="100%"
            height="100%"
            animation="wave"
            style={{ borderRadius: "8px" }}
          />
        </CardContent>
      </Card>
    );
  }

  const qtdQuota = differentQtrData.qtdQuota || 0;
  const qtdACV = differentQtrData.qtdACV || 0;

  // Determine which data source to use for projections
  const useEnsembleData =
    ensembleData !== "" &&
    ensembleData.expectedHighMidLow &&
    ensembleData.lowMidHigh &&
    Array.isArray(ensembleData.lowMidHigh);

  let projectionHigh = 0;
  let projectionMid = 0;
  let projectionLow = 0;

  if (useEnsembleData) {
    // Use EnsembleData if available
    const totalData = ensembleData.lowMidHigh.find(
      (item) => item.category === "Total"
    );
    if (totalData) {
      projectionHigh = Math.round(totalData.high || 0);
      projectionMid = Math.round(totalData.mid || 0);
      projectionLow = Math.round(totalData.low || 0);
    }
  } else if (pipelineCoverageCFQ !== "") {
    // Fall back to PipelineCoverageCFQ
    const companyData =
      pipelineCoverageCFQ.data &&
      Array.isArray(pipelineCoverageCFQ.data) &&
      pipelineCoverageCFQ.data.find((i) => i.name === "company");

    if (companyData) {
      projectionHigh = companyData.projectedAttainmentHigh || 0;
      projectionMid = companyData.projectedAttainmentMid || 0;
      projectionLow = companyData.projectedAttainmentLow || 0;
    }
  }

  // Calculate coverage ratio
  const quotaRemaining = qtdQuota - qtdACV;
  const pipelineACV =
    pipelineCoverageCFQ !== "" &&
    pipelineCoverageCFQ.data &&
    Array.isArray(pipelineCoverageCFQ.data) &&
    pipelineCoverageCFQ.data.find((i) => i.name === "company")
      ? pipelineCoverageCFQ.data.find((i) => i.name === "company").acv
      : 0;

  const coverageRatio = quotaRemaining > 0 ? pipelineACV / quotaRemaining : 0;

  // Get qualified pipeline
  const qualifiedPipeline =
    ensembleData !== "" ? ensembleData.qualified_pipeline || 0 : 0;

  // Format projection values
  const formatProjection = (value) => {
    const formatted = countOrACVFormatterMillion(value || 0, true);
    return formatted.replace(/^\$/, "");
  };

  const highDisplay = formatProjection(projectionHigh);
  const midDisplay = formatProjection(projectionMid);
  const lowDisplay = formatProjection(projectionLow);

  // Calculate percentages
  const highPercent =
    qtdQuota > 0 ? ((projectionHigh / qtdQuota) * 100).toFixed(1) : "0";
  const midPercent =
    qtdQuota > 0 ? ((projectionMid / qtdQuota) * 100).toFixed(1) : "0";
  const lowPercent =
    qtdQuota > 0 ? ((projectionLow / qtdQuota) * 100).toFixed(1) : "0";

  // Format qualified pipeline
  const formattedQualifiedPipeline = countOrACVFormatterMillion(
    qualifiedPipeline,
    true
  );
  const qualifiedPipelineDisplay = formattedQualifiedPipeline.replace(
    /^\$/,
    ""
  );

  // Format coverage ratio
  const coverageRatioDisplay =
    coverageRatio > 0 ? coverageRatio.toFixed(1) : "0.0";

  return (
    <Card className={classes.card}>
      <CardContent className={classes.contentRoot}>
        {/* Left container – Qtr Projection */}
        <Box className={classes.leftContainer}>
          {/* Title */}
          <Box>
            <Typography
              className={typography.textMediumSemibold}
              style={{ color: rccColors.neutral[800] }}
            >
              {title || "Qtr Projection"}
            </Typography>
          </Box>

          {/* 3-Column Grid Layout */}
          <Box className={classes.projectionsGrid}>
            {/* --- H ROW ITEMS --- */}
            <Typography
              className={`${typography.textMediumRegular} ${classes.labelAlign}`}
              style={{ color: rccColors.neutral[500] }}
            >
              H:
            </Typography>
            <Typography
              className={`${typography.textLargeSemibold} ${classes.valueAlign}`}
              style={{
                color: rccColors.neutral[900],
                cursor: onDrilldown ? "pointer" : "default",
              }}
              onClick={onDrilldown}
            >
              {user?.Currency}
              {highDisplay}
            </Typography>
            <span className={`${classes.pill} ${classes.pillGreen}`}>
              <Typography
                component="span"
                className={typography.textSmallSemibold}
              >
                {highPercent}%
              </Typography>
            </span>

            {/* --- M ROW ITEMS --- */}
            <Typography
              className={`${typography.textMediumRegular} ${classes.labelAlign}`}
              style={{ color: rccColors.neutral[500] }}
            >
              M:
            </Typography>
            <Typography
              className={`${typography.textLargeSemibold} ${classes.valueAlign}`}
              style={{
                color: rccColors.neutral[900],
                cursor: onDrilldown ? "pointer" : "default",
              }}
              onClick={onDrilldown}
            >
              {user?.Currency}
              {midDisplay}
            </Typography>
            <span className={`${classes.pill} ${classes.pillLightGreen}`}>
              <Typography
                component="span"
                className={typography.textSmallSemibold}
              >
                {midPercent}%
              </Typography>
            </span>

            {/* --- L ROW ITEMS --- */}
            <Typography
              className={`${typography.textMediumRegular} ${classes.labelAlign}`}
              style={{ color: rccColors.neutral[500] }}
            >
              L:
            </Typography>
            <Typography
              className={`${typography.textLargeSemibold} ${classes.valueAlign}`}
              style={{
                color: rccColors.neutral[900],
                cursor: onDrilldown ? "pointer" : "default",
              }}
              onClick={onDrilldown}
            >
              {user?.Currency}
              {lowDisplay}
            </Typography>
            <span className={`${classes.pill} ${classes.pillYellow}`}>
              <Typography
                component="span"
                className={typography.textSmallSemibold}
              >
                {lowPercent}%
              </Typography>
            </span>
          </Box>
        </Box>

        {/* Right container – Coverage Ratio & Qualified Pipe */}
        <Box className={classes.rightContainer}>
          {/* Coverage Ratio */}
          <Box className={classes.metricBox}>
            <Typography
              className={`${typography.textExtraSmallRegular}`}
              style={{ color: rccColors.neutral[500] }}
            >
              Coverage Ratio
            </Typography>
            <Box className={classes.metricOval}>
              <Typography
                className={typography.textMediumSemibold}
                style={{ color: rccColors.blue[700] }}
              >
                {coverageRatioDisplay}
              </Typography>
            </Box>
          </Box>

          {/* Qualified Pipe */}
          <Box className={classes.metricBox}>
            <Typography
              className={`${typography.textExtraSmallRegular}`}
              style={{ color: rccColors.neutral[500] }}
            >
              Qualified Pipe
            </Typography>
            <Box
              className={classes.metricOval}
              style={{ cursor: onDrilldown ? "pointer" : "default" }}
              onClick={onDrilldown}
            >
              <Typography
                className={typography.textMediumSemibold}
                style={{ color: rccColors.blue[700] }}
              >
                {user?.Currency}
                {qualifiedPipelineDisplay}
              </Typography>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export default QtrProjectionCard;
